import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { WALLET_KEY } from "@/lib/api";
import { connectPhantomAndSignIn, connectEvmAndSignIn } from "@/lib/wallet";

const WALLET_TYPE_KEY = "mj_wallet_type";
const WalletContext = createContext(null);

export const useWallet = () => useContext(WalletContext);

export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState(localStorage.getItem(WALLET_KEY));
  const [walletType, setWalletType] = useState(localStorage.getItem(WALLET_TYPE_KEY) || "demo");
  const [user, setUser] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem(WALLET_KEY)) return;
    try {
      const { data } = await api.get("/users/me");
      setUser(data);
    } catch (e) {
      // stale wallet
    }
  }, []);

  useEffect(() => {
    if (wallet) refreshUser();
  }, [wallet, refreshUser]);

  const persist = useCallback((addr, type, u) => {
    localStorage.setItem(WALLET_KEY, addr);
    localStorage.setItem(WALLET_TYPE_KEY, type);
    setWallet(addr);
    setWalletType(type);
    if (u) setUser(u);
  }, []);

  // Simulated demo wallet (no signing) — kept as a zero-friction fallback.
  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/wallet/connect", {});
      persist(data.wallet, "demo", data);
      return data;
    } finally {
      setConnecting(false);
    }
  }, [persist]);

  const connectPhantom = useCallback(async () => {
    setConnecting(true);
    try {
      const { wallet: addr, user: u } = await connectPhantomAndSignIn();
      persist(addr, "phantom", u);
      return u;
    } finally {
      setConnecting(false);
    }
  }, [persist]);

  const connectEvm = useCallback(async () => {
    setConnecting(true);
    try {
      const { wallet: addr, user: u } = await connectEvmAndSignIn();
      persist(addr, "evm", u);
      return u;
    } finally {
      setConnecting(false);
    }
  }, [persist]);

  const disconnect = useCallback(() => {
    localStorage.removeItem(WALLET_KEY);
    localStorage.removeItem(WALLET_TYPE_KEY);
    setWallet(null);
    setWalletType("demo");
    setUser(null);
  }, []);

  return (
    <WalletContext.Provider
      value={{ wallet, walletType, user, connecting, connect, connectPhantom, connectEvm, disconnect, refreshUser, setUser }}
    >
      {children}
    </WalletContext.Provider>
  );
};
